<template>
  <form novalidate="true" @submit.prevent="submit">
    <b-field
      label="Formulaire"
      :message="fields.liveSetupForm.error"
      :type="fields.liveSetupForm.error !== null ? 'is-danger' : ''"
    >
      <div v-if="liveSetupForms.length === 0 && !loading">Aucun formulaire trouvé</div>
      <b-select
        v-else
        v-model="fields.liveSetupForm.value"
        expanded
        :loading="loading"
        :disabled="loading"
        placeholder="Choisissez un formulaire"
      >
        <option
          v-for="liveSetupForm in liveSetupForms"
          :key="liveSetupForm.id"
          :value="liveSetupForm"
        >
          {{ liveSetupForm.name }}
        </option>
      </b-select>
    </b-field>
    <b-field v-show="liveSetupFormSubmissions.length > 0" label="Soumission">
      <b-select
        v-model="fields.liveSetupFormSubmission.value"
        expanded
        :loading="loading"
        :disabled="loading"
        placeholder="Choisissez une soumission du formulaire"
      >
        <option
          v-for="liveSetupFormSubmission in liveSetupFormSubmissions"
          :key="liveSetupFormSubmission.id"
          :value="liveSetupFormSubmission"
        >
          {{ liveSetupFormSubmission.name }}
        </option>
      </b-select>
      <p class="control">
        <a
          v-if="fields.liveSetupFormSubmission.value"
          :href="fields.liveSetupFormSubmission.value.editUrl"
          target="_blank"
        >
          <b-button type="is-primary" outlined label="Modifier le formulaire" />
        </a>
      </p>
    </b-field>
    <b-field
      v-if="fields.liveSetupFormSubmission.value"
      label="Administrateur"
      :message="fields.administratorId.error"
      :type="fields.administratorId.error !== null ? 'is-danger' : ''"
    >
      <b-select
        v-model="fields.administratorId.value"
        expanded
        placeholder="Choisissez un administrateur"
        icon="user"
      >
        <option v-for="admin in administrators" :key="admin.id" :value="admin.id">
          {{ admin.firstname }} {{ admin.lastname }}
        </option>
      </b-select>
    </b-field>
    <div v-if="liveSetupFormSubmission" class="m-5">
      <o-message
        v-if="submissionErrorMessages.length > 0"
        :errorMessages="submissionErrorMessages"
        title="Formulaire invalide"
        :closable="false"
      />
      <live-setup-step
        v-else
        :liveSetupFormSubmission="liveSetupFormSubmission"
        :errorMessages="submitErrorMessages"
        :live="live"
        :liveSettings="liveSettings"
        :liveSponsors="liveSponsors"
        :liveExternalLinks="liveExternalLinks"
        :liveStages="liveStages"
        :liveCategories="liveCategories"
        :liveAlertContacts="liveAlertContacts"
      />
    </div>
    <div class="level">
      <div class="level-left">
        <p class="level-item">
          <b-button
            v-if="!live"
            native-type="submit"
            type="is-primary"
            :loading="loading"
            :disabled="disabledSubmit"
            label="Importer un live"
          />
          <b-button
            v-else
            type="is-success"
            label="Voir le live"
            :loading="loading"
            @click="
              $router.push({
                name: 'live.edit.informations',
                params: { id: live.id },
              })
            "
          />
        </p>
      </div>
      <div class="level-right">
        <p class="level-item">
          <b-button
            v-if="submissionErrorMessages.length > 0 && fields.liveSetupFormSubmission.value"
            class="mr-0"
            label="Rafraîchir"
            :loading="loading"
            :disabled="loading"
            @click="refreshLiveSetupFormSubmission(fields.liveSetupFormSubmission.value.id)"
          />
        </p>
      </div>
    </div>
  </form>
</template>

<script>
import { mapActions } from 'vuex'
import OMessage from '@components/Message.vue'
import { getInvalidSubmissionErrorMessages } from '@views/pages/LiveSetupFormPage/getInvalidSubmissionErrorMessages.js'
import LiveSetupStep from '@views/pages/LiveSetupFormPage/LiveSetupStep/index.vue'

export default {
  name: 'LiveSetupForm',

  components: { LiveSetupStep, OMessage },

  data() {
    return {
      loading: false,
      liveSetupForms: [],
      liveSetupFormSubmissions: [],
      liveSetupFormSubmission: null,
      submissionErrorMessages: [],
      administrators: [],
      fields: {
        liveSetupForm: {
          value: null,
          error: null,
        },
        liveSetupFormSubmission: {
          value: null,
        },
        administratorId: {
          value: null,
          error: null,
        },
      },
      submitErrorMessages: null,
      live: null,
      liveSettings: null,
      liveSponsors: [],
      liveExternalLinks: [],
      liveAlertContacts: [],
      liveStages: [],
      liveCategories: [],
    }
  },

  async created() {
    await Promise.all([this.fetchLiveSetupForms(), this.fetchAdministrators()])
  },

  watch: {
    async 'fields.liveSetupForm.value'(form) {
      this.liveSetupFormSubmission = null
      this.fields.liveSetupFormSubmission.value = null

      if (form) {
        await this.fetchLiveSetupFormSubmissions(form.id)
      }
    },

    async 'fields.liveSetupFormSubmission.value'(submission) {
      if (submission) {
        await this.refreshLiveSetupFormSubmission(submission.id)
      }
    },

    liveSetupFormSubmissions(liveSetupFormSubmissions) {
      this.fields.liveSetupForm.error =
        liveSetupFormSubmissions?.length === 0 ? 'Aucune soumission pour ce formulaire' : null
    },
  },

  computed: {
    disabledSubmit() {
      return (
        this.loading ||
        this.submissionErrorMessages.length > 0 ||
        this.live ||
        Object.values(this.fields).find((field) => !field.value || field.error)
      )
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    async submit() {
      this.loading = true
      this.submitErrorMessages = null

      try {
        await this.createLive()

        if (this.live) {
          await this.fetchDefaultLiveSettings()
          await this.createLiveSettings()
          await this.createLiveSponsor()
          await this.createLiveSponsors()
          await this.createLiveExternalLinks()
          await this.createLiveStages()
          await this.createLiveCategories()
          await this.createLiveAlertContacts()
        }
      } catch (err) {
        console.error(err)
      }

      this.loading = false
    },

    async createLive() {
      try {
        this.live = await this.$services.liveService.create({
          ...this.liveSetupFormSubmission.live,
          startedAt: new Date(this.liveSetupFormSubmission.live.startedAt),
          endedAt: new Date(this.liveSetupFormSubmission.live.endedAt),
          administratorId: this.fields.administratorId.value,
        })
      } catch (err) {
        console.error(err)
        this.submitErrorMessages = {
          ...this.submitErrorMessages,
          live: err.message,
        }
      }
    },

    async fetchDefaultLiveSettings() {
      try {
        this.liveSettings = await this.$services.liveSettingsService.getByLiveId(this.live.id)
      } catch (err) {
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
        console.error(err)
      }
    },

    async createLiveSettings() {
      try {
        this.liveSettings = await this.$services.liveSettingsService.update(this.live.id, {
          ...this.liveSettings,
          globalCoordinateFormat: this.liveSetupFormSubmission.settings.coordinateFormat,
          privateDefaultMapLayer: this.liveSetupFormSubmission.settings.defaultMapLayer,
          privateVehicleIdleThreshold: this.liveSetupFormSubmission.settings.vehicleIdleThreshold,
          privateAlertTypes: this.liveSetupFormSubmission.settings.alertTypes,
        })
      } catch (err) {
        console.error(err)
        this.submitErrorMessages = {
          ...this.submitErrorMessages,
          liveSettings: err.message,
        }
      }
    },

    async createLiveStages() {
      this.liveStages = (
        await Promise.all(
          this.liveSetupFormSubmission.stages.map(async (stage, index) => {
            try {
              return await this.$services.liveStageService.create({
                ...stage,
                liveId: this.live.id,
                startedAt: new Date(stage.startedAt),
                endedAt: new Date(stage.endedAt),
              })
            } catch (err) {
              console.error(err)
              this.submitErrorMessages = {
                ...this.submitErrorMessages,
                liveStages: {
                  ...this.submissionErrorMessages.stages,
                  [index]: err.message,
                },
              }
            }
          }),
        )
      ).filter((stage) => stage)
    },

    async createLiveCategories() {
      try {
        this.liveCategories = await this.$services.liveCategoryService.createMany(
          this.live.id,
          this.liveSetupFormSubmission.categories,
        )
      } catch (err) {
        console.error(err)
        this.submitErrorMessages = {
          ...this.submitErrorMessages,
          liveCategories: this.liveSetupFormSubmission.categories.reduce(
            (errors, _category, index) => ({
              ...errors,
              [index]: err.message,
            }),
            { ...this.submissionErrorMessages.categories },
          ),
        }
      }
    },

    async createLiveAlertContacts() {
      this.liveAlertContacts = (
        await Promise.all(
          this.liveSetupFormSubmission.alertContacts.map(async (alertContact, index) => {
            try {
              return await this.$services.liveAlertContactService.create({
                ...alertContact,
                liveId: this.live.id,
              })
            } catch (err) {
              console.error(err)
              this.submitErrorMessages = {
                ...this.submitErrorMessages,
                liveAlertContacts: {
                  ...this.submissionErrorMessages.alertContacts,
                  [index]: err.message,
                },
              }
            }
          }),
        )
      ).filter((alertContact) => alertContact)
    },

    async createLiveSponsor() {
      if (!this.liveSetupFormSubmission.live.sponsorUrl) {
        return
      }

      try {
        const image = await this.imageUrlToBase64(this.liveSetupFormSubmission.live.sponsorUrl)
        if (!image) {
          throw new Error('Cannot convert image url to base64')
        }

        await this.$services.liveService.addLiveSponsor(this.live.id, image)
      } catch (err) {
        console.error(err)
        this.submitErrorMessages = {
          ...this.submitErrorMessages,
          liveSponsor: err.message,
        }
      }
    },

    async createLiveSponsors() {
      this.liveSponsors = (
        await Promise.all(
          this.liveSetupFormSubmission.sponsors.map(async (sponsor, index) => {
            try {
              const image = await this.imageUrlToBase64(sponsor.imageUrl)
              if (!image) {
                throw new Error('Cannot convert image url to base64')
              }

              return await this.$services.liveSponsorService.create({
                liveId: this.live.id,
                position: index,
                image,
              })
            } catch (err) {
              console.error(err)
              this.submitErrorMessages = {
                ...this.submitErrorMessages,
                liveSponsors: {
                  ...this.submissionErrorMessages.sponsors,
                  [index]: err.message,
                },
              }
            }
          }),
        )
      ).filter((sponsor) => sponsor)
    },

    async createLiveExternalLinks() {
      this.liveExternalLinks = (
        await Promise.all(
          this.liveSetupFormSubmission.externalLinks.map(async (externalLink, index) => {
            try {
              return await this.$services.liveExternalLinkService.create({
                ...externalLink,
                liveId: this.live.id,
                position: index,
              })
            } catch (err) {
              console.error(err)
              this.submitErrorMessages = {
                ...this.submitErrorMessages,
                liveExternalLinks: {
                  ...this.submissionErrorMessages.externalLinks,
                  [index]: err.message,
                },
              }
            }
          }),
        )
      ).filter((externalLink) => externalLink)
    },

    async fetchLiveSetupForms() {
      this.loading = true

      try {
        this.liveSetupForms = await this.$services.liveSetupFormService.getAllForms()
      } catch (err) {
        console.error(err)
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
      }

      this.loading = false
    },

    async fetchAdministrators() {
      try {
        this.administrators = await this.$services.administratorService.getAll()
      } catch (err) {
        console.error(err)
      }
    },

    async fetchLiveSetupFormSubmissions(formId) {
      this.loading = true

      try {
        this.liveSetupFormSubmissions =
          await this.$services.liveSetupFormService.getAllFormSubmissions(formId)
      } catch (err) {
        console.error(err)
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
      }

      this.loading = false
    },

    async fetchLiveSetupFormSubmission(id) {
      this.loading = true

      try {
        this.liveSetupFormSubmission = await this.$services.liveSetupFormService.getFormSubmission(
          id,
        )
      } catch (err) {
        console.error(err)
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
      }

      this.loading = false
    },

    async refreshLiveSetupFormSubmission(id) {
      await this.fetchLiveSetupFormSubmission(id)
      this.resetLiveSetupFormSubmission()
      this.submissionErrorMessages = getInvalidSubmissionErrorMessages(this.liveSetupFormSubmission)
    },

    resetLiveSetupFormSubmission() {
      this.live = null
      this.liveSettings = null
      this.liveStages = []
      this.liveCategories = []
      this.liveAlertContacts = []
      this.submitErrorMessages = {}
    },

    async imageUrlToBase64(imageUrl) {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()

        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error(error)
        return null
      }
    },
  },
}
</script>
